import { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import ChatWidget from "@/components/ChatWidget";
import SiteHead from "@/components/SiteHead";
import { useSiteConfig } from "@/hooks/useSiteConfig";
import { mediaUrl, platformDisplayHost } from "@/lib/api";
import { normalizeStorefrontTheme } from "@/lib/storefrontTheme";
import { CreateStoreWithTheme } from "@/components/CarPartsOnboardDialog";
import {
  LandingHeader, LandingFooter, LandingPricing, LandingStickyCta,
  landingLogoVars, yearlyPlans, rupee,
} from "@/components/LandingChrome";
import {
  Rocket, Search, Heart, Ticket, Percent, Package, Sparkles,
  ShoppingCart, UserPlus, RefreshCw, IndianRupee, ArrowRight,
  Smartphone, Zap, Star, MoveRight, BadgeCheck, Play, Quote,
  ChevronLeft, ChevronRight, Truck, ClipboardList, MessageCircle,
  Bell, Link2, Layers, Shield, Send, TrendingUp, Tag, Users, ChevronDown,
} from "lucide-react";
import { BRAND_NAME, ORG_SLOGAN, ORG_DESCRIPTION } from "@/lib/brand";

function toEmbed(url) {
  if (!url) return null;
  const yt = url.match(/(?:youtube\.com\/(?:watch\?v=|embed\/|shorts\/)|youtu\.be\/)([\w-]{11})/);
  if (yt) return `https://www.youtube.com/embed/${yt[1]}?autoplay=1&rel=0`;
  const vm = url.match(/vimeo\.com\/(?:video\/)?(\d+)/);
  if (vm) return `https://player.vimeo.com/video/${vm[1]}?autoplay=1`;
  return null;
}

function VideoPlayer({ url, poster, vertical = false, rounded = "rounded-[18px]", className = "border border-black/10 shadow-sm", playTestId = "video-play" }) {
  const [playing, setPlaying] = useState(false);
  const embed = toEmbed(url);
  const aspect = vertical ? "aspect-[9/16]" : "aspect-video";
  return (
    <div className={`relative ${aspect} w-full bg-stone-900 overflow-hidden ${rounded} ${className}`}>
      {!playing ? (
        <button
          type="button"
          onClick={() => url && setPlaying(true)}
          className="group absolute inset-0 w-full h-full"
          data-testid={playTestId}
          aria-label="Play video"
        >
          {poster ? (
            <img src={mediaUrl(poster)} alt="" loading="lazy" decoding="async" className="w-full h-full object-cover" />
          ) : (
            <div className="w-full h-full bg-gradient-to-br from-stone-800 to-stone-950" />
          )}
          <span className="absolute inset-0 bg-black/25 group-hover:bg-black/10 transition-colors" />
          <span className="absolute inset-0 flex items-center justify-center">
            <span className="w-14 h-14 rounded-full bg-white text-stone-900 flex items-center justify-center group-hover:scale-105 transition-transform shadow-lg">
              <Play className="w-6 h-6 ml-0.5 fill-stone-900" />
            </span>
          </span>
        </button>
      ) : embed ? (
        <iframe src={embed} title="video" className="w-full h-full" allow="autoplay; encrypted-media; picture-in-picture" allowFullScreen />
      ) : (
        <video src={url} poster={mediaUrl(poster)} controls autoPlay playsInline className="w-full h-full object-cover" />
      )}
    </div>
  );
}

function Stars({ n = 5 }) {
  return (
    <div className="flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map((i) => (
        <Star key={i} className={`w-4 h-4 ${i <= n ? "text-amber-400 fill-amber-400" : "text-stone-300"}`} />
      ))}
    </div>
  );
}

const FEATURES = [
  { icon: Rocket, title: "Instant ecommerce creation", body: "Pick a URL, add products, go live the same afternoon." },
  { icon: ShoppingCart, title: "Cart, checkout & SSL", body: "A smooth, secure checkout — HTTPS on every store, free." },
  { icon: RefreshCw, title: "Abandoned cart recovery", body: "Win back shoppers who almost bought." },
  { icon: IndianRupee, title: "UPI · Razorpay · Cashfree · COD", body: "Take money the way Indian shoppers actually pay." },
  { icon: Search, title: "SEO built-in", body: "Sitemaps, meta tags & rich results — found on Google." },
  { icon: UserPlus, title: "Login / Signup", body: "Customer accounts & order history out of the box." },
  { icon: Percent, title: "Discounts", body: "Flat or percentage — run offers in seconds." },
  { icon: Heart, title: "Wishlist", body: "Let shoppers save what they love." },
  { icon: Ticket, title: "Coupons", body: "Codes, limits & campaigns made simple." },
  { icon: Package, title: "Growing catalogs", body: "Plenty of room to grow your product list." },
  { icon: Truck, title: "Shipping & delivery", body: "Rates, zones & free-delivery thresholds." },
  { icon: ClipboardList, title: "Order management", body: "Track, update & fulfil every order from one dashboard." },
  { icon: Smartphone, title: "Your own Android app", body: "One-click signed build + Play Store help." },
  { icon: Sparkles, title: "+80 more features", body: "Variants, pages, media, analytics & lots more." },
];

const SWITCH_CARDS = [
  { title: "No Dollar Drama", desc: (price) => `Shopify $29 ≈ ₹2,900 + forex + GST. We charge a flat ${rupee(price)}/year.` },
  { title: "Desi Payments Built-in", desc: () => "UPI intent, COD with WhatsApp confirm, Razorpay, PhonePe, Shiprocket, Delhivery — one toggle." },
  { title: "10x Faster Support", desc: () => "“Bhaiya help chahiye?” Average 2 min reply. Hindi, Hinglish, Tamil. Real humans, not bots." },
];

const FALLBACK_FAQ = [
  { q: "What is Mera Startup (MeraStartup)?", a: "Mera Startup — also written MeraStartup — is India's best Shopify alternative. It is an Indian ecommerce website builder, not a government startup portal. Shopkeepers launch a full online store with cart, UPI, COD, GST billing, SEO and an optional Android app, from ₹1999 per year." },
  { q: "Kya GST bill milega? Is it Indian billing?", a: "Haan. Pure INR billing with GST invoice. ₹1999 means ₹1999 — pay on UPI, invoice instantly." },
  { q: "Can I migrate my existing Shopify store?", a: "Yes. Products, customers and orders can be imported. Our team helps you go live without downtime." },
  { q: "Do you take any commission on sales?", a: "No. 0% commission. Your dukaan, your profit." },
  { q: "Android app sach mein Play Store ready hai?", a: "Haan. Flutter native app with APK + AAB, push notifications, and Play Store submission help." },
  { q: "COD aur courier kaise manage hoga?", a: "Shiprocket, Delhivery and more can be connected. COD confirmation via WhatsApp." },
  { q: "Support kaise dete ho? English only?", a: "Desi support — Hindi, Hinglish and English. Real people, not a bot loop." },
];

const APP_FRAME = "https://i.ibb.co/nMcdgwJ2/Mobile-Frame-Merastartup.webp";

function IndiaIcon({ className = "w-6 h-6" }) {
  return (
    <svg viewBox="0 0 24 24" className={className} fill="none" stroke="currentColor" strokeWidth="1.6" aria-hidden>
      <path d="M12 3c1.2 1.4 2 3.2 2.2 5.2.6-.8 1.6-1.3 2.6-1.1 1 .2 1.6 1.2 1.4 2.2-.4 1.8-1.6 3.4-2.4 5.1-.5 1.1-.6 2.4-.2 3.6.3.9 1 1.6 1.4 2.5-2.1.6-4.4.8-6.6.4-1.2-.2-2.4-.8-3.2-1.7 1-.9 1.4-2.3 1.1-3.6-.4-1.6-1.6-2.8-2.3-4.3C5.4 9.6 6 7.2 7.6 5.8 8.8 4.7 10.4 3.8 12 3Z" />
    </svg>
  );
}

function MonumentBackdrop() {
  return (
    <svg viewBox="0 0 640 280" className="absolute left-1/2 top-8 -translate-x-1/2 w-[min(92%,720px)] h-auto text-stone-400/20 pointer-events-none hidden sm:block" aria-hidden>
      <path fill="none" stroke="currentColor" strokeWidth="1.2" d="M320 28v36m-90 176V140h180v100M140 240V168h80v72m200 0V168h80v72M230 140c0-40 28-72 90-72s90 32 90 72M176 168c0-24 16-44 44-44m200 44c0-24 16-44 44-44M80 240h480M320 64c-10 0-18 8-18 18h36c0-10-8-18-18-18z" />
    </svg>
  );
}

function VideoModal({ open, onOpenChange, url, poster, title }) {
  const embed = toEmbed(url);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl w-[calc(100%-1.5rem)] p-0 overflow-hidden bg-black border-0 sm:rounded-2xl" data-testid="hero-video-modal">
        <DialogTitle className="sr-only">{title || "Intro video"}</DialogTitle>
        <div className="aspect-video w-full bg-black">
          {open && url ? (
            embed ? (
              <iframe src={embed} title={title || "Intro video"} className="w-full h-full" allow="autoplay; encrypted-media; picture-in-picture" allowFullScreen />
            ) : (
              <video src={url} poster={mediaUrl(poster)} controls autoPlay playsInline className="w-full h-full object-cover" />
            )
          ) : (
            <div className="w-full h-full flex items-center justify-center text-white/70 text-sm">No video yet</div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function HeroIntroStage({ intro, host, price, onPlay }) {
  const products = [
    ["#FDE68A", "Kurta", "₹1,499"],
    ["#FBCFE8", "Saree", "₹2,299"],
    ["#FED7AA", "Jewelry", "₹899"],
    ["#C7D2FE", "Sneaker", "₹1,199"],
  ];
  return (
    <div className="relative mx-auto w-full max-w-[480px] lg:max-w-[520px]" data-testid="hero-intro-stage">
      <div className="flex justify-center mb-3">
        <div className="inline-flex items-center gap-1.5 rounded-full bg-white border border-black/10 shadow-sm px-3 py-1.5 text-[11px] font-bold">
          <Zap className="w-3.5 h-3.5 text-[#FF8A2B] fill-[#FF8A2B]" />
          Store Live in 60s
        </div>
      </div>
      <div className="relative rounded-[22px] sm:rounded-[24px] bg-white border border-black/10 shadow-[0_20px_50px_-24px_rgba(0,0,0,0.4)] overflow-hidden">
        <div className="flex items-center gap-1.5 px-3 h-9 bg-white border-b border-black/5">
          <span className="w-2 h-2 rounded-full bg-red-400" />
          <span className="w-2 h-2 rounded-full bg-amber-400" />
          <span className="w-2 h-2 rounded-full bg-emerald-400" />
          <span className="ml-auto text-[10px] text-stone-400 font-mono-nums truncate">{host}</span>
        </div>
        <button
          type="button"
          onClick={onPlay}
          className="relative w-full aspect-video text-left group"
          data-testid="hero-intro-play"
          aria-label="Play intro video"
        >
          {intro?.thumbnail_url ? (
            <img src={mediaUrl(intro.thumbnail_url)} alt="" className="absolute inset-0 w-full h-full object-cover" />
          ) : (
            <div className="absolute inset-0 bg-gradient-to-br from-[#FFF6EA] via-white to-[#E8F8F0]" />
          )}
          <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-black/10 to-transparent" />
          <div className="absolute top-3 left-3 z-10">
            <div className="inline-flex items-center gap-1.5 rounded-full bg-black/80 text-white px-2.5 py-1 text-[10px] font-bold">
              <span className="w-2 h-2 rounded-full bg-red-500 ms-pulse" /> REC • Live
            </div>
          </div>
          <span className="absolute inset-0 flex items-center justify-center z-20">
            <span className="w-14 h-14 sm:w-16 sm:h-16 rounded-full bg-white/90 text-stone-900 flex items-center justify-center shadow-xl group-hover:scale-105 transition-transform">
              <Play className="w-6 h-6 sm:w-7 sm:h-7 ml-0.5 fill-stone-900" />
            </span>
          </span>
        </button>
      </div>
      <div className="flex justify-center -mt-4 relative z-10">
        <div className="inline-flex items-center gap-2 rounded-full bg-[#1C1917] text-white px-3 py-2 shadow-lg text-[11px] font-semibold max-w-full">
          <ShoppingCart className="w-4 h-4 text-[#FF8A2B] shrink-0" />
          <span className="truncate">Order #4921 — COD</span>
          <span className="text-[#2ED4A6] whitespace-nowrap">₹1,499 • Confirmed ✓</span>
        </div>
      </div>
      <div className="mt-4 grid grid-cols-2 sm:grid-cols-5 gap-2">
        {products.map(([c, n, p]) => (
          <div key={n} className="rounded-xl bg-white border border-black/10 shadow-sm overflow-hidden">
            <div className="h-10" style={{ background: c }} />
            <div className="px-1.5 py-1.5">
              <div className="text-[10px] font-semibold truncate">{n}</div>
              <div className="text-[10px] text-emerald-700 font-mono-nums">{p}</div>
            </div>
          </div>
        ))}
        <div className="col-span-2 sm:col-span-1 rounded-xl bg-white border border-black/10 shadow-sm px-2.5 py-2 flex items-center gap-2">
          <span className="w-8 h-8 rounded-full bg-[#2ED4A6] text-white flex items-center justify-center shrink-0">
            <TrendingUp className="w-4 h-4" />
          </span>
          <div className="leading-tight min-w-0">
            <div className="text-[9px] font-bold uppercase tracking-wider text-stone-400">Today</div>
            <div className="text-sm font-extrabold font-mono-nums truncate">₹2,34,500</div>
          </div>
        </div>
      </div>
    </div>
  );
}

const TRUST_ITEMS = [
  { icon: IndiaIcon, title: "Made for India", body: "Built for Indian sellers" },
  { icon: Zap, title: "Instant Setup", body: "Launch in 60 seconds" },
  { icon: Tag, title: "Zero Hidden Fees", body: "What you see is what you pay" },
  { icon: IndianRupee, title: "UPI + COD", body: "Payments the Bharat way" },
  { icon: Users, title: "Trusted by 10K+", body: "Growing every day" },
];

function AppShowcase() {
  return (
    <div className="relative flex justify-center items-center py-8 sm:py-4 min-h-[380px] sm:min-h-[460px]">
      <div className="absolute inset-0 pointer-events-none" style={{ background: "radial-gradient(40% 50% at 55% 55%, rgba(255,138,43,0.22), transparent 70%), radial-gradient(30% 40% at 70% 40%, rgba(46,212,166,0.18), transparent 70%)" }} />
      <div className="absolute top-2 left-0 sm:left-6 z-20 lp-float text-white/80 text-xs flex items-center gap-1.5">
        <Star className="w-4 h-4 text-[#FF8A2B] fill-[#FF8A2B]" />
        <span className="font-bold text-white">4.9</span>
        <span className="hidden sm:inline">Top rated by store owners</span>
      </div>
      <div className="absolute left-1 sm:left-8 bottom-10 z-20 ms-float-d text-center">
        <div className="w-12 h-12 mx-auto rounded-full bg-[#FF8A2B] flex items-center justify-center ms-glow-orange">
          <Play className="w-5 h-5 text-white fill-white ml-0.5" />
        </div>
        <div className="mt-1.5 text-[10px] font-semibold text-white/80">Publish on<br />Play Store</div>
      </div>
      <div className="absolute right-0 sm:right-4 top-16 z-20 ms-float-e space-y-4">
        <div className="flex items-center gap-2">
          <span className="w-10 h-10 rounded-xl bg-[#2ED4A6] text-[#0F0F0F] flex items-center justify-center ms-glow-teal"><Zap className="w-5 h-5" /></span>
          <span className="hidden sm:block text-xs text-white/80">One-click build</span>
        </div>
        <div className="flex items-center gap-2">
          <span className="w-10 h-10 rounded-xl bg-[#2ED4A6] text-[#0F0F0F] flex items-center justify-center ms-glow-teal"><Shield className="w-5 h-5" /></span>
          <span className="hidden sm:block text-xs text-white/80">Signed &amp; secure</span>
        </div>
      </div>
      <div className="absolute right-6 top-6 z-10 hidden sm:block">
        <Send className="w-8 h-8 text-[#2ED4A6] rotate-12" />
      </div>
      <div className="relative z-10 w-[210px] sm:w-[250px] lp-float" data-testid="app-phone-wrap">
        <img src={APP_FRAME} alt="MeraStartup Android app" className="relative z-10 w-full h-auto select-none" data-testid="app-phone-frame" />
        <div className="absolute -bottom-3 left-1/2 -translate-x-1/2 w-36 h-6 rounded-full bg-black/50 blur-md pointer-events-none" />
      </div>
    </div>
  );
}

export default function Landing() {
  const loc = useLocation();
  const cfg = useSiteConfig();
  const pricing = cfg.pricing;
  const themes = cfg.theme_showcase;
  const intro = cfg.intro_video;
  const videoT = cfg.video_testimonials;
  const textT = cfg.text_testimonials;
  const [faqOpen, setFaqOpen] = useState(0);
  const [progress, setProgress] = useState(0);
  const [heroVideoOpen, setHeroVideoOpen] = useState(false);
  const videoRef = useRef(null);
  const scrollVideos = (dir) => videoRef.current?.scrollBy({ left: dir * 300, behavior: "smooth" });

  const plans = yearlyPlans(pricing?.plans);
  const websitePrice = plans.find((p) => p.id === "website")?.price || plans[0]?.price || 1999;
  const appPlan = plans.find((p) => p.id === "website_app") || plans[1];
  const faqItems = (cfg.seo?.faq || []).filter((x) => x?.q && x?.a);
  const faqs = faqItems.length ? faqItems : FALLBACK_FAQ;
  const host = platformDisplayHost() || "merastartup.in";
  const logoVars = landingLogoVars(cfg);
  const onPricing = loc.pathname === "/pricing";

  useEffect(() => {
    if (!onPricing) return;
    const t = setTimeout(() => document.getElementById("pricing")?.scrollIntoView({ behavior: "smooth", block: "start" }), 80);
    return () => clearTimeout(t);
  }, [onPricing]);

  useEffect(() => {
    const onScroll = () => {
      const el = document.documentElement;
      const max = el.scrollHeight - el.clientHeight;
      setProgress(max > 0 ? el.scrollTop / max : 0);
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <div className="ms-landing min-h-screen font-sans antialiased overflow-x-hidden pb-16 md:pb-0" style={logoVars}>
      <SiteHead
        path={onPricing ? "/pricing" : "/"}
        title={onPricing ? `Pricing | ${BRAND_NAME} — website plans in India` : `${BRAND_NAME} — ${ORG_SLOGAN}`}
        description={onPricing ? "Yearly plans for a Shopify-style Indian store. Cart, UPI, COD and optional Android app." : `${ORG_DESCRIPTION}`}
        faq={onPricing ? undefined : faqs}
      />
      <div className="fixed top-0 left-0 h-[2px] z-[60] ms-progress" style={{ width: `${Math.min(100, progress * 100)}%` }} />

      <LandingHeader websitePrice={websitePrice} active={onPricing ? "pricing" : ""} />

      <section className="relative overflow-x-hidden">
        <div className="absolute inset-0 pointer-events-none ms-hero-wash" />
        <MonumentBackdrop />
        <div className="relative max-w-[1280px] mx-auto px-5 lg:px-8 pt-10 pb-8 grid lg:grid-cols-2 gap-10 lg:gap-12 items-start lg:items-center">
          <div>
            <span className="inline-flex items-center gap-2 rounded-full bg-[#E8F8F0] border border-[#2ED4A6]/40 px-3.5 py-1.5 text-[11px] font-bold uppercase tracking-wide text-emerald-900">
              <span className="w-2 h-2 rounded-full bg-[#2ED4A6] ms-pulse" /> 🇮🇳 India&apos;s best alternative to Shopify
            </span>
            <h1 className="ms-serif mt-6 text-[2.45rem] sm:text-5xl lg:text-[64px] leading-[0.98] font-bold tracking-tight">
              <span className="block text-stone-900">MeraStartup.</span>
              <span className="block text-[#E85D04]">Your Dukaan. Your Rules.</span>
              <span className="block text-[#1B7A4A]">
                Built for <span className="ms-bharat">Bharat.</span>
              </span>
            </h1>
            <p className="mt-5 text-base sm:text-lg text-stone-600 max-w-md">
              Launch your online store in 60 seconds and grow your business with zero dollar worries.
            </p>
            <div className="mt-8 flex flex-wrap items-center gap-3">
              <Link to="/signup?plan=website" data-testid="hero-cta-primary">
                <Button className="h-12 px-7 text-base bg-stone-900 hover:bg-stone-800 text-white font-bold rounded-full">
                  Launch Store at {rupee(websitePrice)} <ArrowRight className="w-5 h-5 ml-1" />
                </Button>
              </Link>
              <Link to="/theme">
                <Button variant="ghost" className="h-12 px-6 text-base font-semibold rounded-full border border-black bg-white hover:bg-stone-50">See Live Themes <ArrowRight className="w-4 h-4 ml-1" /></Button>
              </Link>
            </div>
            <div className="mt-8 flex flex-wrap items-center gap-3 text-sm text-stone-600">
              <div className="flex -space-x-2" data-testid="hero-avatars">
                {["A", "B", "C", "D"].map((l, i) => {
                  const src = (cfg.brand?.hero_avatars || [])[i];
                  return (
                    <span key={l} className="w-8 h-8 rounded-full border-2 border-white bg-stone-200 text-[10px] font-bold flex items-center justify-center overflow-hidden">
                      {src ? <img src={mediaUrl(src)} alt="" className="w-full h-full object-cover" data-testid={`hero-avatar-${i}`} /> : l}
                    </span>
                  );
                })}
              </div>
              <div>10,000+ stores • 4.9/5 ★ • UPI • COD</div>
            </div>
          </div>
          <div className="relative w-full lg:pl-2">
            <HeroIntroStage
              intro={intro}
              host={host.replace(/^www\./, "")}
              price={websitePrice}
              onPlay={() => setHeroVideoOpen(true)}
            />
          </div>
        </div>
        <div className="relative border-t border-black/5 bg-white/55">
          <div className="max-w-[1280px] mx-auto px-5 lg:px-8 py-6 grid grid-cols-2 md:grid-cols-5 gap-4" data-testid="hero-trust-bar">
            {TRUST_ITEMS.map((item) => {
              const Icon = item.icon;
              return (
                <div key={item.title} className="flex items-start gap-2.5">
                  <span className="w-9 h-9 rounded-xl bg-[#FFF6EA] border border-black/5 flex items-center justify-center text-[#E85D04] shrink-0">
                    <Icon className="w-4 h-4" />
                  </span>
                  <div>
                    <div className="text-sm font-bold text-stone-900">{item.title}</div>
                    <div className="text-[11px] text-stone-500">{item.body}</div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
        <div className="border-y border-black/10 bg-[#F3EEE4] text-stone-700 overflow-hidden">
          <div className="lp-ticker py-3">
            {["UPI", "COD", "Razorpay", "Cashfree", "Shiprocket", "Delhivery", "GST invoice", "WhatsApp", "Free SSL", "SEO", "Play Store app"].concat(["UPI", "COD", "Razorpay", "Cashfree", "Shiprocket", "Delhivery", "GST invoice", "WhatsApp", "Free SSL", "SEO", "Play Store app"]).map((t, i) => (
              <span key={i} className="mx-6 text-sm font-semibold uppercase tracking-wider inline-flex items-center gap-2">
                <Star className="w-3.5 h-3.5 text-[#FF8A2B] fill-[#FF8A2B]" /> {t}
              </span>
            ))}
          </div>
        </div>
      </section>

      <VideoModal
        open={heroVideoOpen}
        onOpenChange={setHeroVideoOpen}
        url={intro?.video_url}
        poster={intro?.thumbnail_url}
        title={intro?.heading || "What we're about"}
      />

      <section className="max-w-[1280px] mx-auto px-5 lg:px-8 py-20">
        <span className="inline-flex px-3 py-1 rounded-full bg-[#FFEAD1] text-[11px] font-bold">WHY FOUNDERS SWITCH</span>
        <h2 className="ms-serif mt-4 text-3xl lg:text-[52px] leading-[0.95] font-bold">Why founders leave Shopify</h2>
        <p className="mt-3 text-stone-500 max-w-xl">Shopify is great for the US. MeraStartup is built for Bharat — pricing, payments, logistics, support.</p>
        <div className="mt-10 grid md:grid-cols-3 gap-5">
          {SWITCH_CARDS.map((c) => (
            <div key={c.title} className="rounded-[22px] bg-white border border-black/10 p-6 shadow-sm">
              <h3 className="ms-serif text-2xl font-bold">{c.title}</h3>
              <p className="mt-3 text-sm text-stone-600 leading-relaxed">{c.desc(websitePrice)}</p>
            </div>
          ))}
          <div className="md:col-span-3 rounded-[22px] bg-[#0F0F0F] text-[#FFFBF2] p-6 sm:p-8 grid sm:grid-cols-2 gap-6 items-center">
            <div>
              <div className="text-[11px] font-bold uppercase tracking-widest text-[#2ED4A6]">Desi support</div>
              <h3 className="ms-serif mt-2 text-2xl font-bold">Bhaiya help chahiye? COD setting?</h3>
              <p className="mt-2 text-sm text-white/70">Haan bilkul — 2 min mein setup. Hindi, Hinglish, English.</p>
            </div>
            <div className="rounded-2xl bg-white/5 border border-white/10 p-4">
              <div className="flex items-center gap-2 text-xs text-white/70 mb-3"><MessageCircle className="w-4 h-4" /> Priya · Support · Online</div>
              <div className="space-y-2 text-sm">
                <div className="rounded-2xl rounded-tl-sm bg-white/10 px-3 py-2">Bhaiya help chahiye? COD setting?</div>
                <div className="rounded-2xl rounded-tr-sm bg-[#2ED4A6]/20 px-3 py-2 text-[#C7F8E6]">Haan bilkul! 2 min mein setup kar deta hu.</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section id="features" className="max-w-[1280px] mx-auto px-5 lg:px-8 pb-12">
        <span className="inline-flex px-3 py-1 rounded-full bg-[#E8FFF6] text-[11px] font-bold text-emerald-800">EVERYTHING INCLUDED</span>
        <h2 className="ms-serif mt-4 text-3xl sm:text-4xl font-bold">One yearly plan. Packed.</h2>
        <p className="mt-3 text-stone-600 max-w-xl">The tools big brands pay lakhs for — bundled for Indian startups.</p>
        <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {FEATURES.map((f) => {
            const Icon = f.icon;
            return (
              <div key={f.title} className="rounded-[20px] bg-white border border-black/10 p-5 shadow-sm hover:-translate-y-0.5 transition-transform">
                <div className="w-10 h-10 rounded-xl bg-stone-900 text-white flex items-center justify-center"><Icon className="w-5 h-5" /></div>
                <h3 className="mt-4 font-bold text-stone-900">{f.title}</h3>
                <p className="mt-1.5 text-sm text-stone-600">{f.body}</p>
              </div>
            );
          })}
        </div>
      </section>

      <section id="app" className="max-w-[1280px] mx-auto px-5 lg:px-8 pb-8">
        <div className="relative rounded-[28px] bg-[#0B0B0B] text-white overflow-hidden">
          <div className="relative grid lg:grid-cols-2 gap-4 lg:gap-8 p-6 sm:p-10 lg:p-12 items-center">
            <div>
              <span className="inline-flex items-center gap-2 rounded-full bg-[#2ED4A6] text-[#0B0B0B] px-3 py-1 text-[11px] font-extrabold uppercase tracking-wide">
                <Smartphone className="w-4 h-4" /> Play Store ready
              </span>
              <h2 className="mt-5 text-3xl sm:text-4xl font-bold leading-tight">
                Your store&apos;s own <span className="text-[#FF8A2B]">Android app</span>{" "}
                <span className="ms-script font-bold text-[1.35em] text-white">in one click.</span>
              </h2>
              <p className="mt-4 text-stone-300 max-w-md">
                Add the Flutter app for {rupee(Math.max(0, (appPlan?.price || 5998) - websitePrice))}/year extra.
                We build a signed app and help you get it on the Play Store.
              </p>
              <ul className="mt-6 space-y-3">
                {[
                  { icon: Smartphone, title: "Native Flutter APK + AAB", body: "Optimized for speed & performance." },
                  { icon: Bell, title: "Push notifications", body: "Stay connected with customers." },
                  { icon: Link2, title: "UPI intent + deep linking", body: "Seamless payments & navigation." },
                  { icon: Layers, title: "Same catalog, orders & payments", body: "Sync everything with your website." },
                ].map((t) => {
                  const Icon = t.icon;
                  return (
                    <li key={t.title} className="flex items-start gap-3">
                      <span className="w-10 h-10 rounded-xl bg-[#2ED4A6]/15 text-[#2ED4A6] flex items-center justify-center shrink-0"><Icon className="w-5 h-5" /></span>
                      <span>
                        <span className="block text-sm font-bold text-white">{t.title}</span>
                        <span className="block text-xs text-white/55">{t.body}</span>
                      </span>
                    </li>
                  );
                })}
              </ul>
              <div className="mt-8 flex flex-col sm:flex-row sm:items-center gap-3">
                <Link to="/signup?plan=website_app">
                  <Button className="h-12 px-7 bg-[#FF8A2B] hover:bg-[#f47a18] text-white font-bold rounded-full ms-glow-orange">
                    Get website + app <span className="ml-2 w-7 h-7 rounded-full bg-white text-[#FF8A2B] inline-flex items-center justify-center"><MoveRight className="w-4 h-4" /></span>
                  </Button>
                </Link>
                <span className="ms-script text-lg text-[#2ED4A6]">Launch your app. Grow your business.</span>
              </div>
            </div>
            <AppShowcase />
          </div>
        </div>
      </section>

      <section id="themes" className="max-w-[1280px] mx-auto px-5 lg:px-8 py-20">
        <span className="inline-flex px-3 py-1 rounded-full bg-[#FFEAD1] text-[11px] font-bold">50+ LOOKS</span>
        <h2 className="ms-serif mt-4 text-3xl sm:text-4xl font-bold">{themes?.heading || "Themes that look made-to-order"}</h2>
        {themes?.subheading && <p className="mt-3 text-stone-600 max-w-xl">{themes.subheading}</p>}
        <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {(themes?.items || []).map((t, i) => {
            const themeId = normalizeStorefrontTheme(t.storefront_theme);
            return (
              <div key={t.id || i} data-testid={`theme-card-${i}`} className="group rounded-[22px] bg-white border border-black/10 overflow-hidden flex flex-col shadow-sm hover:-translate-y-0.5 transition-transform">
                <div className="h-48 bg-stone-100 overflow-hidden">
                  {t.image_url && <img src={mediaUrl(t.image_url)} alt={t.title} loading="lazy" decoding="async" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />}
                </div>
                <div className="p-5 flex-1 flex flex-col">
                  <div className="flex items-center justify-between gap-2">
                    <h3 className="font-bold text-lg">{t.title}</h3>
                    <span className="text-[10px] font-bold uppercase tracking-wider rounded-full border border-black/15 px-2 py-0.5">{themeId}</span>
                  </div>
                  <p className="mt-1.5 text-sm text-stone-600 flex-1">{t.description}</p>
                  <div className="mt-4 flex flex-col sm:flex-row gap-2">
                    <a href={t.url || "#"} target="_blank" rel="noreferrer" data-testid={`theme-demo-${i}`} className="inline-flex items-center justify-center gap-1.5 text-sm font-bold text-[#c2410c]">
                      View demo <ArrowRight className="w-4 h-4" />
                    </a>
                    <CreateStoreWithTheme themeId={themeId} testId={`theme-create-${i}`} className="inline-flex items-center justify-center gap-1.5 rounded-full bg-stone-900 text-white text-sm font-bold px-4 py-2 hover:bg-[#FF8A2B] transition-colors">
                      Create Store
                    </CreateStoreWithTheme>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {videoT && (videoT.items || []).length > 0 && (
        <section id="testimonials" className="bg-[#0F0F0F] text-[#FFFBF2] rounded-t-[28px] lg:rounded-t-[40px] py-16 lg:py-20">
          <div className="max-w-[1280px] mx-auto px-5 lg:px-8">
            <span className="inline-flex px-3 py-1 rounded-full bg-white/10 text-[11px] font-bold">FOUNDER STORIES</span>
            <h2 className="ms-serif mt-4 text-3xl lg:text-[52px] leading-[0.95] font-bold">{videoT.heading || "Founders Don't Lie. Revenue Does."}</h2>
            {videoT.subheading && <p className="mt-3 text-white/60 max-w-xl">{videoT.subheading}</p>}
            <div className="relative mt-10">
              <button type="button" onClick={() => scrollVideos(-1)} aria-label="Previous" data-testid="video-prev" className="hidden sm:flex absolute -left-3 top-1/2 -translate-y-1/2 z-10 w-11 h-11 items-center justify-center rounded-full bg-white text-stone-900">
                <ChevronLeft className="w-5 h-5" />
              </button>
              <button type="button" onClick={() => scrollVideos(1)} aria-label="Next" data-testid="video-next" className="hidden sm:flex absolute -right-3 top-1/2 -translate-y-1/2 z-10 w-11 h-11 items-center justify-center rounded-full bg-white text-stone-900">
                <ChevronRight className="w-5 h-5" />
              </button>
              <div ref={videoRef} className="flex gap-4 sm:gap-6 overflow-x-auto no-scrollbar snap-x snap-mandatory pb-2 scroll-smooth">
                {(videoT.items || []).map((v, i) => (
                  <div key={v.id || i} data-testid={`video-testimonial-${i}`} className="snap-center shrink-0 w-[72vw] sm:w-[260px]">
                    <div className="rounded-[24px] border-[5px] border-white overflow-hidden shadow-[0_12px_40px_rgba(0,0,0,0.35)] bg-black">
                      <VideoPlayer url={v.video_url} poster={v.thumbnail_url} vertical rounded="rounded-[19px]" className="border-0" playTestId={`video-testimonial-play-${i}`} />
                    </div>
                    <div className="mt-3 px-1 text-center sm:text-left">
                      {v.name && <div className="font-bold leading-tight">{v.name}</div>}
                      {v.role && <div className="text-sm text-white/60">{v.role}</div>}
                    </div>
                  </div>
                ))}
              </div>
              <div className="flex sm:hidden items-center justify-center gap-3 mt-4">
                <button type="button" onClick={() => scrollVideos(-1)} aria-label="Previous" data-testid="video-prev-m" className="w-11 h-11 flex items-center justify-center rounded-full bg-white text-stone-900"><ChevronLeft className="w-5 h-5" /></button>
                <button type="button" onClick={() => scrollVideos(1)} aria-label="Next" data-testid="video-next-m" className="w-11 h-11 flex items-center justify-center rounded-full bg-white text-stone-900"><ChevronRight className="w-5 h-5" /></button>
              </div>
            </div>
          </div>
        </section>
      )}

      {textT && (textT.items || []).length > 0 && (
        <section id="reviews" className="max-w-[1280px] mx-auto px-5 lg:px-8 py-20">
          <span className="inline-flex px-3 py-1 rounded-full bg-[#FFEAD1] text-[11px] font-bold">REVIEWS</span>
          <h2 className="ms-serif mt-4 text-3xl sm:text-4xl font-bold">{textT.heading || "Loved by Indian startups"}</h2>
          {textT.subheading && <p className="mt-3 text-stone-600 max-w-xl">{textT.subheading}</p>}
          <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {(textT.items || []).map((r, i) => (
              <div key={r.id || i} data-testid={`text-testimonial-${i}`} className="rounded-[22px] bg-white border border-black/10 p-5 flex flex-col shadow-sm">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-full overflow-hidden bg-stone-200 flex-shrink-0">
                    {r.image_url ? <img src={mediaUrl(r.image_url)} alt={r.title} className="w-full h-full object-cover" /> : <span className="w-full h-full flex items-center justify-center font-bold">{(r.title || "M").slice(0, 1)}</span>}
                  </div>
                  <div className="min-w-0">
                    <div className="font-bold text-stone-900 truncate">{r.title}</div>
                    <div className="text-[11px] font-semibold text-emerald-600">VERIFIED ✓</div>
                  </div>
                </div>
                <div className="mt-3"><Stars n={Number(r.rating) || 5} /></div>
                <p className="mt-3 text-sm text-stone-700 leading-relaxed flex-1">
                  <Quote className="w-4 h-4 text-[#FF8A2B] inline -mt-1 mr-1" />
                  {r.text}
                </p>
              </div>
            ))}
          </div>
        </section>
      )}

      <LandingPricing pricing={pricing} plans={plans} />

      <section className="max-w-[1280px] mx-auto px-5 lg:px-8 pb-16" id="faq" data-testid="landing-faq">
        <div className="grid lg:grid-cols-[0.9fr_1.1fr] gap-8">
          <div>
            <span className="inline-flex px-3 py-1 rounded-full bg-[#FFEAD1] text-[11px] font-bold">FAQ</span>
            <h2 className="ms-serif mt-4 text-3xl lg:text-[44px] leading-[0.95] font-bold">Seedhe sawaal,<br />seedhe jawaab.</h2>
            <p className="mt-3 text-stone-500 text-sm max-w-sm">No sales fluff. Real questions from founders who switched.</p>
          </div>
          <div className="space-y-3">
            {faqs.map((item, i) => {
              const open = faqOpen === i;
              return (
                <div key={i} className="rounded-[18px] border border-black/10 bg-white shadow-sm overflow-hidden">
                  <button type="button" className="w-full text-left px-5 py-4 font-bold text-stone-900 flex items-center justify-between gap-3" onClick={() => setFaqOpen(open ? -1 : i)} data-testid={`faq-q-${i}`}>
                    {item.q}
                    <ChevronDown className={`w-4 h-4 shrink-0 transition-transform ${open ? "rotate-180" : ""}`} />
                  </button>
                  {open ? <p className="px-5 pb-4 text-sm text-stone-600 leading-relaxed">{item.a}</p> : null}
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <section className="max-w-[1280px] mx-auto px-5 lg:px-8 pb-20">
        <div className="rounded-[28px] bg-[#0F0F0F] text-white p-8 sm:p-14 relative overflow-hidden">
          <div className="relative grid lg:grid-cols-2 gap-8 items-center">
            <div>
              <div className="text-[11px] font-bold uppercase tracking-widest text-[#2ED4A6]">7-day live · no dollar trap</div>
              <h2 className="ms-serif mt-3 text-3xl sm:text-5xl font-bold leading-[0.95]">Claim your store.</h2>
              <p className="mt-3 text-white/70 max-w-md">Your store live in minutes. GST bill. UPI. COD. App ready. Specially designed for Indian startups.</p>
              <Link to="/signup?plan=website">
                <Button className="mt-7 h-12 px-8 bg-[#FF8A2B] hover:bg-[#f47a18] text-white font-bold rounded-full">
                  Start selling — {rupee(websitePrice)}/year <ArrowRight className="w-5 h-5 ml-1" />
                </Button>
              </Link>
            </div>
            <div className="rounded-2xl bg-white/5 border border-white/10 p-5">
              <div className="text-xs text-white/50 mb-2">yourstore.{host.replace(/^www\./, "")}</div>
              <div className="ms-serif text-2xl">Your store is live!</div>
              <div className="mt-1 text-sm text-white/60">Products, orders, UPI &amp; COD ready.</div>
              <div className="mt-4 inline-flex items-center gap-2 text-sm font-bold text-[#2ED4A6]"><BadgeCheck className="w-4 h-4" /> Made in India</div>
            </div>
          </div>
        </div>
      </section>

      <LandingFooter />

      <LandingStickyCta websitePrice={websitePrice} />

      <ChatWidget />
    </div>
  );
}
